import {
  getCurrentScope,
  onScopeDispose,
  onWatcherCleanup,
  readonly,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue';

export const LOADING_INDICATOR_DELAY_MS = 250;
export const LOADING_INDICATOR_MIN_VISIBLE_MS = 400;

export interface DelayedFlagOptions {
  delay?: number;
  minVisible?: number;
}

export function useDelayedFlag(
  source: MaybeRefOrGetter<boolean>,
  options: DelayedFlagOptions = {},
): Readonly<Ref<boolean>> {
  const delay = options.delay ?? LOADING_INDICATOR_DELAY_MS;
  const minVisible = options.minVisible ?? LOADING_INDICATOR_MIN_VISIBLE_MS;

  const visible = ref(false);
  let shownAt = 0;
  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  const cancelHide = () => {
    if (hideTimer !== null) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
  };

  const show = () => {
    visible.value = true;
    shownAt = Date.now();
  };

  watch(
    () => toValue(source),
    (on) => {
      if (on) {
        cancelHide();
        if (visible.value) {
          return;
        }
        if (delay <= 0) {
          show();
          return;
        }
        const timer = setTimeout(show, delay);
        onWatcherCleanup(() => clearTimeout(timer));
        return;
      }

      if (!visible.value) {
        return;
      }
      const remaining = minVisible - (Date.now() - shownAt);
      if (remaining <= 0) {
        visible.value = false;
        return;
      }
      cancelHide();
      hideTimer = setTimeout(() => {
        visible.value = false;
        hideTimer = null;
      }, remaining);
    },
    { immediate: true },
  );

  if (getCurrentScope()) {
    onScopeDispose(cancelHide);
  }

  return readonly(visible);
}
